import type { TaskAction } from "@gw/shared-types";
import { fmtET } from "@/lib/format";

interface Props {
  actions: TaskAction[];
  /** Duration unit from the screen toggle; drives whether Wait/Active show bd or cd values. */
  unit: "bd" | "cd";
}

export function ActionHistoryTable({ actions, unit }: Props) {
  return (
    <table className="task-action-tbl">
      {/* Trailing block — Wait 96 + Execution 96 + Duration 96 + Reason 120 = 408px — matches the
          parent Tasks table's trailing block (…+ Actions 120), so the Wait/Execution/Duration trio
          lines up under the parent header. Action/Role/Actor are auto (absorb remaining width). */}
      <colgroup>
        <col style={{ width: 36 }} />
        <col />
        <col />
        <col />
        <col style={{ width: 180 }} />
        <col style={{ width: 180 }} />
        <col style={{ width: 180 }} />
        <col style={{ width: 96 }} />
        <col style={{ width: 96 }} />
        <col style={{ width: 96 }} />
        <col style={{ width: 120 }} />
      </colgroup>
      <thead>
        <tr>
          <th>#</th>
          <th>Action</th>
          <th>Role</th>
          <th>Actor</th>
          <th>Assigned</th>
          <th>Started</th>
          <th>Completed</th>
          <th className="numeric">Wait</th>
          <th className="numeric">Execution</th>
          <th className="numeric">Duration</th>
          <th>Reason</th>
        </tr>
      </thead>
      <tbody>
        {actions.map((a) => {
          const wait = unit === "cd" ? a.waitCd : a.waitBd;
          const execution = unit === "cd" ? a.activeCd : a.activeBd;
          const duration = wait + execution;
          return (
          <tr key={a.actionIndex}>
            <td>{a.actionIndex}</td>
            <td>
              <strong>{a.actionType}</strong>
            </td>
            <td>
              <span className={"actor-role-pill " + a.actorRole.toLowerCase()}>
                {a.actorRole}
              </span>
            </td>
            <td>{a.actorName}</td>
            <td>
              <span className="cell-secondary">{fmtET(a.actionAssignedAt)}</span>
            </td>
            <td>
              <span className="cell-secondary">{fmtET(a.actionStartDatetime)}</span>
            </td>
            <td>
              {a.actionCompletedDatetime ? (
                <span className="cell-secondary">{fmtET(a.actionCompletedDatetime)}</span>
              ) : a.actionStartDatetime ? (
                // Started but not yet completed → pulsing running indicator.
                <span className="dur-running">—</span>
              ) : (
                // Not started by any actor yet → plain em-dash, no pulse.
                <span style={{ color: "var(--fg-3)" }}>—</span>
              )}
            </td>
            <td className="numeric">{wait ? `${wait.toFixed(2)} ${unit}` : "—"}</td>
            <td className="numeric">{execution ? `${execution.toFixed(2)} ${unit}` : "—"}</td>
            <td className="numeric">{duration ? `${duration.toFixed(2)} ${unit}` : "—"}</td>
            <td style={{ color: "var(--fg-2)", fontSize: 11, overflowWrap: "anywhere" }}>
              {a.reasonMessage || "—"}
            </td>
          </tr>
          );
        })}
      </tbody>
    </table>
  );
}
